import React, { useMemo } from 'react';
import styled from "styled-components"
// import { useAuthContext } from "../AuthProvider"
import { Button } from "./Button"
import ProPlanTable from "../assets/pro_plan_features.png"
import { darkBlueBackground, yellow } from "./colors"
import { useStore } from "@nanostores/react"
import { $auth, logoutUser } from "../store/auth"
import { SectionTransparent } from "../ui/SectionTransparent"
import dayjs from "dayjs"
import { useStripeCustomerGetNextInvoiceQuery } from '../queries';
import { CancelSubscription } from '../profile/CancelSubscription';
import { Spin } from 'antd';
import currency from 'currency.js';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 66vh;
`
const Image = styled.img`
  height: 100%;
  width: auto;
  margin: 5rem;
`

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const CurrentState = styled.div`
  font-size: 2rem;
  color: ${yellow};
  text-align: center;
  font-weight: 600;
  margin: 3rem;
  width: 100%;
`

const TextLine = styled.div`
  color: #fff;
  font-size: 1.5rem;
  padding-top: 10px;
`

const SubscriptionOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const LicenseKeyOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const LicenseKeyInputContainer = styled.div<{ $isOpen: boolean }>`
  display: ${props => (props.$isOpen ? "flex" : "none")};
  align-items: end;
  justify-content: center;
`

const SectionContainerTransparent = styled(SectionTransparent)`
  max-width: 800px;
  text-align: center;
  font-size: 1.3rem;
  gap: 16px;
  width: 100%;
  margin-bottom: 20px;
`

const PlanCard = styled.div`
    background-image: linear-gradient(50deg, ${yellow} 0%, rgba(255, 255, 0, 0.51) 100%);
    border-radius: 16px;
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`

export const ProfilePage: React.FC<{ onUpgrade: () => void }> = ({ onUpgrade }) => {
  const userState = useStore($auth)
  const [
    licenseKeyValidationOpen,
    setLicenseKeyValidationOpen
  ] = React.useState<boolean>(false)
  const [licenseKey, setLicenseKey] = React.useState<string>("")

  const navigate = useNavigate()

  const { isPro, purchaseDefault } = {
    isPro: false,
    purchaseDefault: async () => ({
      successMessage: "Purchase successful!",
      errorMessage: undefined
    })
  } // useAuthContext()
  // const { isPro, purchaseDefault } = useAuthContext()

  const { data: nextInvoiceData, isLoading: nextInvoiceIsLoading, error: nextInvoiceError } = useStripeCustomerGetNextInvoiceQuery({ enabled: true })
  const nextPaymentDate = useMemo(() => {
    if (nextInvoiceData && nextInvoiceData?.nextInvoice?.nextPaymentAttempt) {
      return {
        date: dayjs.unix(nextInvoiceData.nextInvoice.nextPaymentAttempt).format('DD / MMM / YYYY'),
        amount: currency(nextInvoiceData.nextInvoice.amountDue, { fromCents: true, symbol: '€', precision: 2 }).format()
      }
    }

    return null
  /*  const subscription = userState.user?.customerInfo?.entitlements?.['active']?.['Pro']
    if (!subscription) {
      return null
    }
    return dayjs(subscription.expirationDate).add(1, 'day').format('DD/MM/YYYY')*/
  }, [nextInvoiceData])

  if (userState.isLoggedIn) {
    return (
      <SectionContainerTransparent style={{ margin: '20px', gap: 0 }}  title={'Account'}>
        <CurrentState style={{ margin: 0}}>
          Profile
        </CurrentState>
        <div>
          <TextLine style={{ color: '#fff', paddingTop: '10px', fontSize: '1.5rem' }}>
            {userState.user?.userAttributes?.given_name} {userState.user?.userAttributes?.family_name}
          </TextLine>
          <TextLine style={{ fontSize: '1.2rem' }}>
            {userState.user?.userAttributes?.email}
          </TextLine>
        </div>
        <CurrentState style={{ margin: '2rem 0' }}>
          Subscription
        </CurrentState>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {userState.user?.isBasic && !userState.user.isPro && (
            <div>
              <PlanCard style={{ width: '320px' }}>
                <TextLine style={{ fontSize: '1.6rem', fontWeight: 'bold', color: darkBlueBackground, padding: 0 }}>
                  Basic
                </TextLine>
              </PlanCard>
            </div>
          )}
          {userState.user?.isBasic && userState.user?.isPro && (
            <PlanCard style={{ width: '320px' }}>
              <TextLine style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '10px', padding: 0, color: darkBlueBackground }}>
                Pro
              </TextLine>
              {nextInvoiceIsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40px' }}>
                  <Spin spinning={true} percent={'auto'} size={"default"} />
                </div>
              ) : (
                <>
                  {nextPaymentDate && (
                    <TextLine style={{ fontSize: '1rem', background: 'rgba(0,0,0,0.4)', padding: '8px 16px', borderRadius: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px'}}>
                        <div style={{ textAlign: 'left', fontWeight: 600 }}>
                          Next payment:
                          <br />
                          <div style={{paddingTop: '3px' }}>
                            {nextPaymentDate?.date}
                          </div>
                        </div>
                        <div>{nextPaymentDate.amount}</div>
                      </div>
                    </TextLine>
                  )}
                </>
              )}
              <>
              {nextInvoiceError && (
                <>
                  <TextLine style={{ color: '#000', fontSize: '1rem', marginTop: '10px' }}>
                    Expires at: {dayjs(userState.user?.customerInfo?.entitlements?.['active']?.['Pro']?.expirationDate).format('DD/MM/YYYY HH:mm')}
                  </TextLine>
                </>
              )}
              {nextInvoiceData && !nextInvoiceError && !nextInvoiceData.nextInvoice && ['trialing', 'active', 'canceled'].includes(nextInvoiceData.status) && (
                <TextLine style={{ color: '#000', fontSize: '1rem', marginTop: '10px' }}>
                  Expires at: {dayjs.unix(nextInvoiceData?.currentPeriodEnd || 0).format('DD/MM/YYYY HH:mm')}
                </TextLine>
              )}
              </>
            </PlanCard>
          )}
          {userState.user?.isPro && !nextInvoiceIsLoading && !nextInvoiceError && nextInvoiceData?.nextInvoice && (
            <CancelSubscription />
          )}
          {!userState.user?.isPro && (
            <div>
              <TextLine style={{ fontSize: '1rem', marginTop: '10px' }}>
                Unlock all features by subscribing to the Pro plan!
              </TextLine>
              <Button onClick={onUpgrade}>
                Unlock all features
              </Button>
            </div>
          )}
        </div>
        <div style={{ textAlign: "center", marginTop: "1rem", width: '100%' }}>
          <Button
            large={true}
            secondary={true}
            onClick={() => {
              logoutUser(() => {
                navigate('/auth')
              })
            }}
          >
            Log out
          </Button>
        </div>
      </SectionContainerTransparent>
    )
  }

  return null

  // return (
  //   <Container>
  //     <Image src={ProPlanTable} alt="Pro Plan Features" />
  //     <ActionContainer>
  //       {!isPro && (
  //         <SubscriptionOption>
  //           <CurrentState>
  //             You're on the basic plan right now. Subscribe to the pro plan to
  //             unlock all features!
  //           </CurrentState>
  //           <Button
  //             onClick={async () => {
  //               if (purchaseDefault) {
  //                 const {
  //                   errorMessage,
  //                   successMessage
  //                 } = await purchaseDefault()
  //                 console.log(
  //                   "Purchase result:",
  //                   JSON.stringify({
  //                     errorMessage,
  //                     successMessage
  //                   })
  //                 )
  //               } else {
  //                 console.error("Purchase function is not defined")
  //               }
  //             }}
  //           >
  //             Subscribe now!
  //           </Button>
  //         </SubscriptionOption>
  //       )}
  //       {isPro && (
  //         <SubscriptionOption>
  //           <CurrentState>
  //             You're on the Pro plan right now. Enjoy!
  //           </CurrentState>
  //         </SubscriptionOption>
  //       )}
  //       <LicenseKeyOption>
  //         <CurrentState>
  //           If you've already purchased a license key, click here to use it!
  //         </CurrentState>
  //         <Button
  //           onClick={() => {
  //             setLicenseKeyValidationOpen(true)
  //           }}
  //         >
  //           Get access with license key
  //         </Button>
  //       </LicenseKeyOption>
  //       <LicenseKeyInputContainer $isOpen={licenseKeyValidationOpen}>
  //         <TextInput
  //           label="license key"
  //           onChange={setLicenseKey}
  //           value={licenseKey}
  //         />
  //         <Button
  //           onClick={() => {
  //             console.log("Submitting the license key", licenseKey)
  //           }}
  //         >
  //           Submit
  //         </Button>
  //       </LicenseKeyInputContainer>
  //     </ActionContainer>
  //   </Container>
  // )
}
